import { lazy, Suspense } from 'react';
import { AuthGuard, PermissionSwitch } from '@emfood/shared';
import { Outlet, Navigate, createRoute, SearchSchemaInput } from '@tanstack/react-router';

import { PERMISSIONS } from 'src/api/roles-config';
import DashboardLayout from 'src/layouts/dashboard';

import { SplashScreen, LoadingScreen } from 'src/components/loading-screen';

import { IssueLogSearchParamsSchema } from 'src/sections/issue-log/search-params';

import { paths } from '../paths';
import { rootRoute } from './root';

const OrderIssueLog = lazy(() => import('src/sections/issue-log/view'));
const OrderIssueDetails = lazy(() => import('src/sections/issue-log/issue-details/view'));
const BuyerOrgIssueLog = lazy(() => import('src/sections/issue-log/buyer-org/view'));
const BuyerOrgIssueDetails = lazy(() => import('src/sections/issue-log/buyer-org/issue-details/view'));
const OrderIssueAnonymousReplyDetails = lazy(
  () => import('src/sections/issue-log/issue-details/anonymous-view')
);
const AnonymousOrderIssueSuccess = lazy(
  () => import('src/sections/issue-log/issue-details/anonymous-success-view')
);

const orderIssuesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'order-issues',
  component: () => (
    <AuthGuard
      splashScreen={<SplashScreen />}
      loginPath={paths.auth.jwt.login}
      pathOnAuthProblem={paths.auth.jwt.login}
    >
      <DashboardLayout>
        <Suspense fallback={<LoadingScreen />}>
          <Outlet />
        </Suspense>
      </DashboardLayout>
    </AuthGuard>
  ),
});

// Distributor Admins and Buyer Admins share the same URLs but see their own side of the issue.
const orderIssuesLogRoute = createRoute({
  getParentRoute: () => orderIssuesRoute,
  path: '/',
  validateSearch: (search: unknown & SearchSchemaInput) =>
    IssueLogSearchParamsSchema.validateSync(search),
  component: () => (
    <PermissionSwitch
      options={[
        {
          permissions: [PERMISSIONS.DISTRIBUTORS_READ_ORDER_ISSUES],
          component: <OrderIssueLog />,
        },
        {
          permissions: [PERMISSIONS.BUYER_ORGS_READ_ORDER_ISSUES],
          component: <BuyerOrgIssueLog />,
        },
      ]}
      fallback={<Navigate to="/403" replace />}
    />
  ),
});

const orderIssueDetailsRoute = createRoute({
  getParentRoute: () => orderIssuesRoute,
  path: '/$issueId',
  component: () => (
    <PermissionSwitch
      options={[
        {
          permissions: [PERMISSIONS.DISTRIBUTORS_READ_ORDER_ISSUES],
          component: <OrderIssueDetails />,
        },
        {
          permissions: [PERMISSIONS.BUYER_ORGS_READ_ORDER_ISSUES],
          component: <BuyerOrgIssueDetails />,
        },
      ]}
      fallback={<Navigate to="/403" replace />}
    />
  ),
});

export const orderIssuesRouteTree = orderIssuesRoute.addChildren([
  orderIssuesLogRoute,
  orderIssueDetailsRoute,
]);

const orderIssuesAnonymousRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'order-issues-email',
  component: () => (
    <Suspense fallback={<LoadingScreen />}>
      <Outlet />
    </Suspense>
  ),
});

type AnonymousReplySearch = {
  accept?: boolean;
};

const orderIssueAnonymousReplyRoute = createRoute({
  getParentRoute: () => orderIssuesAnonymousRoute,
  path: '/$emailReplyId',
  component: OrderIssueAnonymousReplyDetails,
  validateSearch: (search: Record<string, unknown>): AnonymousReplySearch => ({
    accept: search.accept === true || search.accept === 'true' ? true : undefined,
  }),
});

const orderIssueAnonymousReplySuccessRoute = createRoute({
  getParentRoute: () => orderIssuesAnonymousRoute,
  path: '/success',
  component: AnonymousOrderIssueSuccess,
});

export const orderIssuesAnonymousRouteTree = orderIssuesAnonymousRoute.addChildren([
  orderIssueAnonymousReplyRoute,
  orderIssueAnonymousReplySuccessRoute,
]);
